import { solveCramer4 } from './solvers.ts';
import type { Vec4 } from '../types.ts';

const G = 9.80665;
const SC_MAX = 4;

interface SCConsts {
  m: Vec4; a: Vec4; b: Vec4; I: Vec4; mu: Vec4;
}

export function scConstants(
  m0: number, L0: number, a0: number, r: number, n: number,
): SCConsts {
  const b0 = L0 - a0;
  const s: SCConsts = {
    m: { x: 0, y: 0, z: 0, w: 0 },
    a: { x: 0, y: 0, z: 0, w: 0 },
    b: { x: 0, y: 0, z: 0, w: 0 },
    I: { x: 0, y: 0, z: 0, w: 0 },
    mu: { x: 0, y: 0, z: 0, w: 0 },
  };
  const vec = [s.m, s.a, s.b];
  let ri = 1;
  for (let i = 0; i < SC_MAX; i++) {
    if (i < n) {
      const idx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
      for (const v of vec) v[idx[i]] = 0;
      s.m[idx[i]] = m0 * ri;
      s.a[idx[i]] = a0 * ri;
      s.b[idx[i]] = b0 * ri;
    }
    ri *= r;
  }
  const mArr = [s.m.x, s.m.y, s.m.z, s.m.w];
  const aArr = [s.a.x, s.a.y, s.a.z, s.a.w];
  const bArr = [s.b.x, s.b.y, s.b.z, s.b.w];
  const iArr = [s.I.x, s.I.y, s.I.z, s.I.w];
  const muArr = [s.mu.x, s.mu.y, s.mu.z, s.mu.w];
  for (let j = 0; j < SC_MAX; j++) {
    if (j < n) {
      let above = 0;
      for (let k = j + 1; k < SC_MAX; k++) above += mArr[k];
      iArr[j] = mArr[j] * aArr[j] * aArr[j] + bArr[j] * bArr[j] * above;
      muArr[j] = mArr[j] * aArr[j] - bArr[j] * above;
    } else {
      iArr[j] = 1; muArr[j] = 0;
    }
  }
  const idx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
  for (let i = 0; i < SC_MAX; i++) { s.I[idx[i]] = iArr[i]; s.mu[idx[i]] = muArr[i]; }
  return s;
}

function scMbase(s: SCConsts, j: number, k: number): number {
  if (j === k) {
    const idx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
    return s.I[idx[j]];
  }
  const lo = Math.min(j, k), hi = Math.max(j, k);
  const bIdx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
  const muIdx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
  return -s.b[bIdx[lo]] * s.mu[muIdx[hi]];
}

export function sculptureDerivatives(
  sa: number[], sb: number[],
  m0: number, L0: number, a0: number, r: number, n: number,
): { da: number[]; db: number[] } {
  const s = scConstants(m0, L0, a0, r, n);

  const th = [sa[0], sa[2], sb[0], sb[2]];
  const om = [sa[1], sa[3], sb[1], sb[3]];

  const A: number[][] = Array.from({ length: 4 }, () => Array(4).fill(0));
  for (let j = 0; j < SC_MAX; j++) {
    for (let k = 0; k < SC_MAX; k++) {
      A[j][k] = scMbase(s, j, k) * Math.cos(th[j] - th[k]);
    }
  }

  const f: number[] = [0, 0, 0, 0];
  for (let l = 0; l < SC_MAX; l++) {
    let fl = -G * s.mu[(['x', 'y', 'z', 'w'] as const)[l]] * Math.sin(th[l]);
    for (let k = 0; k < SC_MAX; k++) {
      fl -= scMbase(s, l, k) * Math.sin(th[l] - th[k]) * om[k] * om[k];
    }
    f[l] = fl;
  }

  const al = solveCramer4(A, f);
  return {
    da: [om[0], al[0], om[1], al[1]],
    db: [om[2], al[2], om[3], al[3]],
  };
}

export function sculptureTip(
  t0: number, t1: number, t2: number, t3: number,
  m0: number, L0: number, a0: number, r: number, n: number,
): { x: number; y: number } {
  const s = scConstants(m0, L0, a0, r, n);
  const th = [t0, t1, t2, t3];
  let ax = 0, ay = 0;
  const last = n - 1;
  for (let i = 0; i < SC_MAX; i++) {
    if (i >= last) break;
    const bIdx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
    ax -= s.b[bIdx[i]] * Math.sin(th[i]);
    ay += s.b[bIdx[i]] * Math.cos(th[i]);
  }
  const aIdx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
  return {
    x: ax + s.a[aIdx[last]] * Math.sin(th[last]),
    y: ay - s.a[aIdx[last]] * Math.cos(th[last]),
  };
}

export function sculptureNodes(
  t0: number, t1: number, t2: number, t3: number,
  m0: number, L0: number, a0: number, r: number, n: number,
): Array<{ x: number; y: number }> {
  const s = scConstants(m0, L0, a0, r, n);
  const th = [t0, t1, t2, t3];
  const aIdx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
  const bIdx: (keyof Vec4)[] = ['x', 'y', 'z', 'w'];
  const nodes: Array<{ x: number; y: number }> = [];
  let ax = 0, ay = 0;
  for (let i = 0; i < n; i++) {
    const ai = s.a[aIdx[i]];
    const bi = s.b[bIdx[i]];
    nodes.push({ x: ax + ai * Math.sin(th[i]), y: ay - ai * Math.cos(th[i]) });
    ax -= bi * Math.sin(th[i]);
    ay += bi * Math.cos(th[i]);
  }
  return nodes;
}
